import React, { JSX, useCallback, useEffect, useState } from 'react'
import { ArrowPathIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { AddStaffMemberResult, RemoveStaffMemberResult, StaffList, StaffMember } from 'types/api'
import listStaffMembersQuery from 'graphql/listStaffMembers.graphql'
import addStaffMemberMutation from 'graphql/addStaffMember.graphql'
import removeStaffMemberMutation from 'graphql/removeStaffMember.graphql'

export const STAFF_LIST_COPY = {
  title: 'Staff list',
  body: 'Staff powers need two keys: a staff role (superuser or admin) in Auth0 and a row on this list. Removing a row ends that person\'s staff powers at their next action, without waiting for their sign-in to run out. Add by user email: the person must have signed in to FlightDeck once and already hold the staff role in Auth0.',
  enforced: 'Enforced on this stage: staff powers need the Auth0 role and a row here.',
  logOnly: 'Log only on this stage: staff powers still come from the Auth0 role alone. Anyone with a staff role who is not on the list is written to the logs, so the list can be checked before this stage enforces it.',
  callerNotListed: 'Your own account is not on the list. Add it before this stage enforces the list, or you will lose your staff powers.',
  empty: 'No one is on the staff list yet.',
  noAccount: 'No FlightDeck account found',
  you: '(you)',
  placeholder: 'user@example.com',
  add: 'Add',
  remove: 'Remove',
  confirmRemove: 'Confirm remove',
  cancel: 'Cancel',
  refresh: 'Refresh',
  loadError: 'Failed to load the staff list',
  addError: 'Failed to add to the staff list',
  removeError: 'Failed to remove from the staff list'
} as const

export const staffModeLine = (mode: string): string =>
  mode === 'ENFORCE' ? STAFF_LIST_COPY.enforced : STAFF_LIST_COPY.logOnly

export const staffAddResultCopy = (email: string, res: AddStaffMemberResult): string =>
  res.alreadyListed ? `${email} was already on the staff list.` : `${email} is now on the staff list.`

export const staffRemoveResultCopy = (email: string | null, res: RemoveStaffMemberResult): string => {
  const who = email ?? 'That account'
  return res.removed ? `${who} is no longer on the staff list.` : `${who} was not on the staff list.`
}

export const staffAddedLine = (member: StaffMember): string | null => {
  if (member.createdAt == null) return null
  const by = member.addedByEmail != null ? ` by ${member.addedByEmail}` : ''
  return `Added ${formatLocalDateTime(member.createdAt)}${by}`
}

export const requestStaffList = async (auth0Token: string): Promise<StaffList> => {
  const { result } = await graphqlRequest<{ result: StaffList }>(listStaffMembersQuery, {}, auth0Token)
  return result
}

export const requestAddStaffMember = async (auth0Token: string, email: string): Promise<AddStaffMemberResult> => {
  const { result } = await graphqlRequest<{ result: AddStaffMemberResult }>(addStaffMemberMutation, { email }, auth0Token)
  return result
}

export const requestRemoveStaffMember = async (auth0Token: string, accountId: string): Promise<RemoveStaffMemberResult> => {
  const { result } = await graphqlRequest<{ result: RemoveStaffMemberResult }>(removeStaffMemberMutation, { accountId }, auth0Token)
  return result
}

export const StaffListCardView = ({
  list,
  loading,
  loadError,
  email,
  adding,
  removingId,
  confirmingId,
  resultText,
  error,
  onEmailChange,
  onAdd,
  onRefresh,
  onAskRemove,
  onConfirmRemove,
  onCancelRemove,
  onDismissError
}: {
  list: StaffList | null
  loading: boolean
  loadError: string | null
  email: string
  adding: boolean
  removingId: string | null
  confirmingId: string | null
  resultText: string | null
  error: string | null
  onEmailChange: (value: string) => void
  onAdd: () => void
  onRefresh: () => void
  onAskRemove: (accountId: string) => void
  onConfirmRemove: (accountId: string) => void
  onCancelRemove: () => void
  onDismissError: () => void
}): JSX.Element => {
  const busy = adding || removingId != null
  const addDisabled = busy || email.trim().length === 0
  return (
    <section
      data-testid='staff-list-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center gap-2'>
        <UserGroupIcon className='size-5 text-brand' aria-hidden='true' />
        <h3 className='flex-1 text-base font-semibold text-dm-text-primary'>{STAFF_LIST_COPY.title}</h3>
        <button
          type='button'
          onClick={onRefresh}
          disabled={loading}
          data-testid='staff-list-refresh-button'
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          <ArrowPathIcon className='size-3.5' aria-hidden='true' />
          {STAFF_LIST_COPY.refresh}
        </button>
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>{STAFF_LIST_COPY.body}</p>
      {list != null && (
        <p
          data-testid='staff-list-mode'
          className={`mt-3 text-xs ${list.mode === 'ENFORCE' ? 'text-emerald-300' : 'text-amber-300'}`}
        >
          {staffModeLine(list.mode)}
        </p>
      )}
      {list != null && !list.callerListed && (
        <p data-testid='staff-list-caller-warning' className='mt-2 text-xs font-semibold text-amber-300'>
          {STAFF_LIST_COPY.callerNotListed}
        </p>
      )}

      <div className='mt-4'>
        {loading && list == null && <LoadingSpinner />}
        {loadError != null && (
          <div data-testid='staff-list-load-error'>
            <ErrorInline>{loadError}</ErrorInline>
          </div>
        )}
        {list != null && list.members.length === 0 && (
          <p data-testid='staff-list-empty' className='text-xs text-dm-text-secondary'>{STAFF_LIST_COPY.empty}</p>
        )}
        {list != null && list.members.length > 0 && (
          <ul data-testid='staff-list-rows' className='divide-y divide-white/5 rounded-md border border-white/10'>
            {list.members.map((member) => {
              const added = staffAddedLine(member)
              const confirming = confirmingId === member.accountId
              const removing = removingId === member.accountId
              return (
                <li
                  key={member.accountId}
                  data-testid='staff-list-row'
                  className='flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center'
                >
                  <div className='min-w-0 flex-1'>
                    <p className='truncate text-sm text-dm-text-primary'>
                      {member.email ?? STAFF_LIST_COPY.noAccount}
                      {member.isYou && <span className='ml-1.5 text-xs text-dm-text-secondary'>{STAFF_LIST_COPY.you}</span>}
                    </p>
                    {added != null && <p className='text-xs text-dm-text-secondary'>{added}</p>}
                  </div>
                  {!member.isYou && (
                    <div className='flex shrink-0 items-center gap-2'>
                      {confirming
                        ? (
                          <>
                            <button
                              type='button'
                              onClick={() => onConfirmRemove(member.accountId)}
                              disabled={busy}
                              data-testid='staff-list-confirm-remove-button'
                              className='inline-flex items-center gap-1.5 rounded-md bg-red-500/80 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60'
                            >
                              {removing && <LoadingSpinner />}
                              {STAFF_LIST_COPY.confirmRemove}
                            </button>
                            <button
                              type='button'
                              onClick={onCancelRemove}
                              disabled={busy}
                              data-testid='staff-list-cancel-remove-button'
                              className='rounded-md border border-white/10 px-2.5 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                            >
                              {STAFF_LIST_COPY.cancel}
                            </button>
                          </>
                          )
                        : (
                          <button
                            type='button'
                            onClick={() => onAskRemove(member.accountId)}
                            disabled={busy}
                            data-testid='staff-list-remove-button'
                            className='rounded-md border border-white/10 px-2.5 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                          >
                            {STAFF_LIST_COPY.remove}
                          </button>
                          )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className='mt-4 flex flex-col gap-2 sm:flex-row sm:items-center'>
        <input
          type='email'
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder={STAFF_LIST_COPY.placeholder}
          disabled={busy}
          data-testid='staff-list-email-input'
          className='flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
        />
        <button
          type='button'
          onClick={onAdd}
          disabled={addDisabled}
          data-testid='staff-list-add-button'
          className='inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {adding && <LoadingSpinner />}
          {STAFF_LIST_COPY.add}
        </button>
      </div>
      {resultText != null && (
        <p data-testid='staff-list-result' className='mt-3 text-xs text-emerald-300'>
          {resultText}
        </p>
      )}
      {error != null && (
        <div className='mt-3' data-testid='staff-list-error'>
          <ErrorInline onDismiss={onDismissError}>{error}</ErrorInline>
        </div>
      )}
    </section>
  )
}

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback

export const StaffListCard = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [list, setList] = useState<StaffList | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [adding, setAdding] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [resultText, setResultText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setLoadError(null)
    try {
      setList(await requestStaffList(auth0Token))
    } catch (err) {
      setLoadError(messageOf(err, STAFF_LIST_COPY.loadError))
    } finally {
      setLoading(false)
    }
  }, [auth0Token])

  useEffect(() => { void load() }, [load])

  const add = async (): Promise<void> => {
    const trimmed = email.trim()
    if (trimmed.length === 0 || adding || removingId != null) return
    setAdding(true)
    setError(null)
    setResultText(null)
    try {
      const res = await requestAddStaffMember(auth0Token, trimmed)
      setResultText(staffAddResultCopy(trimmed, res))
      setEmail('')
      await load()
    } catch (err) {
      setError(messageOf(err, STAFF_LIST_COPY.addError))
    } finally {
      setAdding(false)
    }
  }

  const remove = async (accountId: string): Promise<void> => {
    if (adding || removingId != null) return
    const rowEmail = list?.members.find((m) => m.accountId === accountId)?.email ?? null
    setRemovingId(accountId)
    setError(null)
    setResultText(null)
    try {
      const res = await requestRemoveStaffMember(auth0Token, accountId)
      setResultText(staffRemoveResultCopy(rowEmail, res))
      setConfirmingId(null)
      await load()
    } catch (err) {
      setError(messageOf(err, STAFF_LIST_COPY.removeError))
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <StaffListCardView
      list={list}
      loading={loading}
      loadError={loadError}
      email={email}
      adding={adding}
      removingId={removingId}
      confirmingId={confirmingId}
      resultText={resultText}
      error={error}
      onEmailChange={setEmail}
      onAdd={() => { void add() }}
      onRefresh={() => { void load() }}
      onAskRemove={(accountId) => { setConfirmingId(accountId); setError(null) }}
      onConfirmRemove={(accountId) => { void remove(accountId) }}
      onCancelRemove={() => setConfirmingId(null)}
      onDismissError={() => setError(null)}
    />
  )
}
